import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import TitleIcon from "@mui/icons-material/Title";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTheme } from "@mui/material/styles";
import { getMarkdownContentSx } from "../../utils/markdownContentSx";

type MarkdownEditorFieldProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  minRows?: number;
};

function wrapSelection(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  before: string,
  after: string = before
): { nextValue: string; nextSelectionStart: number; nextSelectionEnd: number } {
  const selected = value.slice(selectionStart, selectionEnd);
  const nextValue =
    value.slice(0, selectionStart) +
    before +
    selected +
    after +
    value.slice(selectionEnd);
  const nextSelectionStart = selectionStart + before.length;
  const nextSelectionEnd = nextSelectionStart + selected.length;
  return { nextValue, nextSelectionStart, nextSelectionEnd };
}

function insertAtCursor(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insertion: string
): { nextValue: string; nextCursor: number } {
  const nextValue =
    value.slice(0, selectionStart) + insertion + value.slice(selectionEnd);
  return { nextValue, nextCursor: selectionStart + insertion.length };
}

const MarkdownEditorField: React.FC<MarkdownEditorFieldProps> = ({
  value,
  onChange,
  label = "Contenu",
  helperText,
  minRows = 14,
}) => {
  const theme = useTheme();
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const applyTransform = useCallback(
    (
      transform: (
        val: string,
        start: number,
        end: number
      ) => {
        nextValue: string;
        nextSelectionStart?: number;
        nextSelectionEnd?: number;
        nextCursor?: number;
      }
    ) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const result = transform(value, start, end);
      onChange(result.nextValue);
      requestAnimationFrame(() => {
        el.focus();
        if (result.nextSelectionStart != null && result.nextSelectionEnd != null) {
          el.setSelectionRange(result.nextSelectionStart, result.nextSelectionEnd);
        } else if (result.nextCursor != null) {
          el.setSelectionRange(result.nextCursor, result.nextCursor);
        }
      });
    },
    [onChange, value]
  );

  const toolbarButtons = [
    {
      title: "Gras",
      icon: <FormatBoldIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) => wrapSelection(val, start, end, "**")),
    },
    {
      title: "Italique",
      icon: <FormatItalicIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) => wrapSelection(val, start, end, "*")),
    },
    {
      title: "Titre",
      icon: <TitleIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) => {
          const lineStart = val.lastIndexOf("\n", start - 1) + 1;
          const lineEnd = val.indexOf("\n", end);
          const endLine = lineEnd === -1 ? val.length : lineEnd;
          const line = val.slice(lineStart, endLine).replace(/^#+\s*/, "");
          const nextValue =
            val.slice(0, lineStart) + "### " + line + val.slice(endLine);
          return {
            nextValue,
            nextSelectionStart: lineStart + 4,
            nextSelectionEnd: lineStart + 4 + line.length,
          };
        }),
    },
    {
      title: "Liste",
      icon: <FormatListBulletedIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) =>
          insertAtCursor(val, start, end, "\n- ")
        ),
    },
    {
      title: "Lien",
      icon: <LinkIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) =>
          wrapSelection(val, start, end, "[", "](https://)")
        ),
    },
    {
      title: "Séparateur",
      icon: <HorizontalRuleIcon fontSize="small" />,
      action: () =>
        applyTransform((val, start, end) =>
          insertAtCursor(val, start, end, "\n\n---\n\n")
        ),
    },
  ];

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={600}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="body2" color="text.secondary">
          {helperText}
        </Typography>
      )}

      <Tabs
        value={tab}
        onChange={(_, next: "edit" | "preview") => setTab(next)}
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
      >
        <Tab label="Éditer" value="edit" />
        <Tab label="Aperçu" value="preview" />
      </Tabs>

      {tab === "edit" && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {toolbarButtons.map((btn) => (
              <Tooltip key={btn.title} title={btn.title}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={btn.action}
                  sx={{ minWidth: 36, px: 1 }}
                >
                  {btn.icon}
                </Button>
              </Tooltip>
            ))}
          </Stack>
          <TextField
            inputRef={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            multiline
            minRows={minRows}
            fullWidth
            placeholder="Saisissez le message au format Markdown…"
            sx={{
              "& .MuiInputBase-root": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
                lineHeight: 1.6,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Markdown : **gras**, *italique*, ### titres, listes avec -, liens
            [texte](url), séparateurs ---
          </Typography>
        </Stack>
      )}

      {tab === "preview" && (
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            minHeight: minRows * 24,
            ...getMarkdownContentSx(theme),
          }}
        >
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Aucun contenu à prévisualiser.
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};

export default MarkdownEditorField;
